import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, IconButton, InputAdornment, InputLabel,
  LinearProgress, Link as EnlaceMui, MenuItem, Pagination, Paper, Select, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { formatearTamano } from '../lib/eventos.js';
import { describirEstado } from '../lib/estados.js';
import { CeldaIdoneidad } from '../components/CeldaIdoneidad.js';
import { DialogoVincular } from '../components/DialogoVincular.js';
import type {
  AnalisisRevisado, ApiFallo, CandidataRevisada, EstadoDescarga, FilaRevision, PlanRevisado,
  Valoracion, VeredictoRevision,
} from '../lib/api.js';

/** Revisión de una biblioteca de Plex (D-28): cada película que ya tienes, juzgada con la
 *  misma vara que colorea la ficha, y lo que toca hacer con ella: nada, pasarla por el
 *  conversor para que las teles la reproduzcan directo, o volver a bajarla porque
 *  HispaShare tiene algo mejor. Nada se hace solo: cada fila propone y aquí se pulsa. */

type ColorChip = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

/** Cómo se pinta cada veredicto y en qué orden sale: arriba lo que pide una decisión. */
const VEREDICTOS: Record<VeredictoRevision, { etiqueta: string; color: ColorChip; orden: number; nota: string }> = {
  redescargar: {
    etiqueta: 'Volver a descargar', color: 'primary', orden: 0,
    nota: 'HispaShare tiene una versión mejor en algo y no peor en nada.',
  },
  valorar: {
    etiqueta: 'A valorar', color: 'secondary', orden: 1,
    nota: 'HispaShare tiene una versión mejor en una cosa y peor en otra: decide tú.',
  },
  convertir: {
    etiqueta: 'Convertir', color: 'warning', orden: 2,
    nota: 'La versión vale, pero el fichero no es directo: pasa por el conversor sin bajar nada.',
  },
  a_mano: {
    etiqueta: 'A mano', color: 'default', orden: 3,
    nota: 'El item de Plex tiene varios ficheros (versiones o partes): no se toca uno solo a ciegas.',
  },
  sin_ficha: {
    etiqueta: 'Sin ficha', color: 'default', orden: 4,
    nota: 'No se encontró la ficha en HispaShare: solo se sabe si hay que convertir. Se puede vincular a mano.',
  },
  error: { etiqueta: 'Error', color: 'error', orden: 5, nota: 'No se pudo revisar; el motivo está en la fila.' },
  sin_fichero: { etiqueta: 'Sin fichero', color: 'error', orden: 6, nota: 'La ruta no existe para nosotros.' },
  bien: {
    etiqueta: 'Bien', color: 'success', orden: 7,
    nota: 'El fichero ya es lo que la conversión produciría, y el sitio no tiene nada mejor.',
  },
};

const ORDEN_VEREDICTOS = (Object.keys(VEREDICTOS) as VeredictoRevision[]).sort(
  (a, b) => VEREDICTOS[a].orden - VEREDICTOS[b].orden,
);

/** Los filtros: uno por veredicto, y uno de OTRO eje —si la película se pudo cruzar con
 *  su ficha de HispaShare—. Va aparte porque el veredicto lo tapa: una película sin cruce
 *  que además hay que convertir sale como «Convertir», y el fallo del cruce se queda en
 *  sus motivos. Es lo único que se arregla a mano (vincular), igual que «Sin vincular» en
 *  la watchlist. */
type Filtro = 'todas' | VeredictoRevision | 'sin_cruzar';

const TAMANOS_PAGINA = [25, 50, 100] as const;

function normalizarTexto(t: string): string {
  return t
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Busca en la lista, no en HispaShare: título, año, nombre de fichero e IMDb. Cada palabra
 *  tecleada tiene que estar, en cualquier orden y sin acentos. */
function coincideConBusqueda(f: FilaRevision, termino: string): boolean {
  const palabras = normalizarTexto(termino).split(' ').filter(Boolean);
  if (palabras.length === 0) return true;
  const pajar = normalizarTexto([f.title, f.year, f.filePath.split('/').pop(), f.imdbId].filter(Boolean).join(' '));
  return palabras.every((palabra) => pajar.includes(palabra));
}

/** Se buscó la ficha y no se encontró. Las filas sin fichero o con error no llegaron a
 *  buscarla, así que no cuentan como «sin cruzar». */
function sinCruzar(f: FilaRevision): boolean {
  return f.hispashareId === null && f.veredicto !== 'sin_fichero' && f.veredicto !== 'error';
}

/** Qué acción tiene sentido ofrecer en una fila. `valorar` ofrece las dos: es justo lo que
 *  hay que decidir. */
function accionesDe(f: FilaRevision): ('redescargar' | 'convertir')[] {
  switch (f.veredicto) {
    case 'redescargar':
      return f.plan ? ['redescargar', 'convertir'] : ['redescargar'];
    case 'valorar':
      return ['redescargar', 'convertir'];
    case 'convertir':
      return ['convertir'];
    default:
      return [];
  }
}

/** Se puede marcar para actuar en bloque: tiene una acción pendiente, o una Plex Version
 *  que borrar. */
function seleccionable(f: FilaRevision): boolean {
  return (accionesDe(f).length > 0 && !pedida(f)) || f.versionesPlexBytes > 0;
}

/** Lo que se pidió desde esta fila sigue en marcha: no se ofrece pedirlo otra vez. */
const NOTA_PEDIDA =
  'Ya se pidió algo desde esta fila y sigue en marcha, así que no se ofrece otra cosa: dos ' +
  'acciones sobre el mismo fichero acabarían sustituyéndolo dos veces. Para cambiar de idea, ' +
  'elimina lo pendiente en Conversiones (o cancela la descarga en Descargas) y los botones vuelven.';

function pedida(f: FilaRevision): boolean {
  return !!f.downloadStatus && !['imported', 'cancelled', 'failed'].includes(f.downloadStatus);
}

const mbps = (v: number | null | undefined) => (v == null ? null : `${v.toFixed(1).replace('.', ',')} Mbps`);

function Idiomas({ codigos, outlined }: { codigos: string[]; outlined?: boolean }) {
  if (!codigos.length) return <>—</>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {codigos.map((l) => (
        <Chip key={l} size="small" variant={outlined ? 'outlined' : 'filled'} label={l.toUpperCase()} />
      ))}
    </Stack>
  );
}

/** Las cinco celdas de D-21 del fichero. El contenedor no se juzga en un fichero de la
 *  biblioteca —la conversión lo cambia—, así que va como detalle del vídeo y no en su
 *  propia celda. */
function CeldasDelFichero({ v, a }: { v: Valoracion; a: AnalisisRevisado | null }) {
  const audios = [...new Set(a?.audio.map((p) => p.idioma ?? 'und') ?? [])];
  const subs = [...new Set(a?.subtitulos.filter((s) => !s.forzado).map((s) => s.idioma ?? 'und') ?? [])];
  const forzados = [...new Set(a?.subtitulos.filter((s) => s.forzado).map((s) => s.idioma ?? 'und') ?? [])];
  return (
    <>
      <CeldaIdoneidad
        juicio={v.resolucion}
        valor={a?.alto ? `${a.ancho ?? '?'}×${a.alto}` : '—'}
        detalle={a?.hdr ?? undefined}
      />
      <CeldaIdoneidad
        juicio={v.codec}
        valor={a?.videoCodec?.toUpperCase() ?? '—'}
        detalle={a?.container ? a.container.toUpperCase() : undefined}
      />
      <CeldaIdoneidad
        juicio={v.audio}
        valor={<Idiomas codigos={audios} />}
        detalle={[...new Set(a?.audio.map((p) => p.codec.toUpperCase()) ?? [])].join(' · ') || undefined}
      />
      <CeldaIdoneidad
        juicio={v.subtitulos}
        valor={
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {subs.map((l) => <Chip key={l} size="small" variant="outlined" label={l.toUpperCase()} />)}
            {forzados.map((l) => <Chip key={`f-${l}`} size="small" variant="outlined" label={`forz. ${l.toUpperCase()}`} />)}
            {!subs.length && !forzados.length && '—'}
          </Stack>
        }
        detalle={a?.subtitulos.some((s) => s.esBitmap) ? 'de imagen' : undefined}
      />
      <CeldaIdoneidad juicio={v.tamano} valor={mbps(a?.bitrateMbps) ?? '—'} />
    </>
  );
}

/** Las mismas cinco celdas para la candidata de HispaShare, al lado, para ver de un vistazo
 *  en qué gana. */
function CeldasDeCandidata({ c }: { c: CandidataRevisada }) {
  const v = c.valoracion;
  return (
    <>
      <CeldaIdoneidad juicio={v.resolucion} valor={c.resolution ?? '—'} />
      <CeldaIdoneidad juicio={v.codec} valor={c.videoCodec ?? '—'} detalle={c.container?.toUpperCase()} />
      <CeldaIdoneidad juicio={v.audio} valor={<Idiomas codigos={c.audioLangs} />} />
      <CeldaIdoneidad juicio={v.subtitulos} valor={<Idiomas codigos={c.subtitleLangs} outlined />} />
      <CeldaIdoneidad juicio={v.tamano} valor={formatearTamano(c.sizeBytes)} />
    </>
  );
}

function describirPlan(p: PlanRevisado): string[] {
  const lineas: string[] = [];
  if (p.video) lineas.push(`Vídeo: ${p.video.accion} (${p.video.motivo})${p.reescalarA ? `, reescalado a ${p.reescalarA}p` : ''}.`);
  for (const a of p.audio) lineas.push(`Audio ${a.idioma ?? 'sin idioma'} (${a.codec}): ${a.accion} — ${a.motivo}.`);
  for (const s of p.subtitulos) lineas.push(`Subtítulo ${s.idioma ?? 'sin idioma'} (${s.codec}): ${s.accion} — ${s.motivo}.`);
  lineas.push(...p.avisos);
  return lineas;
}

function FilaPelicula({
  f,
  marcada,
  onMarcar,
  abierta,
  onAbrir,
  onAccion,
  onVincular,
  onBorrarVersiones,
  ocupada,
}: {
  f: FilaRevision;
  marcada: boolean;
  onMarcar: (v: boolean) => void;
  abierta: boolean;
  onAbrir: () => void;
  onAccion: (tipo: 'redescargar' | 'convertir' | 'revisar') => void;
  onVincular: () => void;
  onBorrarVersiones: () => void;
  ocupada: boolean;
}) {
  const v = VEREDICTOS[f.veredicto];
  const acciones = accionesDe(f);
  const yaPedida = pedida(f);
  const columnas = 9;
  return (
    <Fragment>
      <TableRow hover sx={{ '& > td': { borderBottom: abierta ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={marcada}
            disabled={!seleccionable(f)}
            onChange={(e) => onMarcar(e.target.checked)}
          />
        </TableCell>
        <TableCell sx={{ minWidth: 220 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {f.title}
            {f.year && <Typography component="span" color="text.secondary"> ({f.year})</Typography>}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" noWrap sx={{ maxWidth: 360 }} title={f.filePath}>
            {f.filePath.split('/').pop()}
            {f.sizeBytes != null && ` · ${formatearTamano(f.sizeBytes)}`}
          </Typography>
          {f.versionesPlexBytes > 0 && (
            <Tooltip title="Lo que ocupa la versión optimizada que Plex creó de esta película. Cuando el fichero ya es directo, sobra.">
              <Chip size="small" variant="outlined" label={`Plex Version · ${formatearTamano(f.versionesPlexBytes)}`} sx={{ mt: 0.5 }} />
            </Tooltip>
          )}
        </TableCell>
        {f.valoracion ? (
          <CeldasDelFichero v={f.valoracion} a={f.analisis} />
        ) : (
          <TableCell colSpan={5}>
            <Typography variant="caption" color="text.secondary">{f.error ?? f.motivos[0] ?? '—'}</Typography>
          </TableCell>
        )}
        <TableCell>
          <Tooltip title={v.nota}>
            <Chip size="small" color={v.color} label={v.etiqueta} />
          </Tooltip>
          {yaPedida && f.downloadStatus && (
            <Tooltip title={NOTA_PEDIDA}>
              <Chip
                size="small"
                variant="outlined"
                sx={{ ml: 0.5 }}
                label={`Ya pedida · ${
                  describirEstado({
                    status: f.downloadStatus as EstadoDescarga,
                    sourcesTotal: 0,
                    sourcesXfer: 0,
                    errorCode: null,
                  }).texto
                }`}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            {!yaPedida && acciones.includes('redescargar') && (
              <Button size="small" variant="contained" disabled={ocupada} onClick={() => onAccion('redescargar')}>
                Descargar
              </Button>
            )}
            {!yaPedida && acciones.includes('convertir') && (
              <Button size="small" variant="outlined" disabled={ocupada} onClick={() => onAccion('convertir')}>
                Convertir
              </Button>
            )}
            <IconButton size="small" onClick={onAbrir} aria-label={abierta ? 'Cerrar detalle' : 'Ver detalle'}>
              <ExpandMoreIcon sx={{ transform: abierta ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columnas} sx={{ p: 0, borderBottom: abierta ? undefined : 'none' }}>
          <Collapse in={abierta} unmountOnExit>
            <Stack spacing={2} sx={{ p: 2, bgcolor: 'action.hover' }}>
              {yaPedida && (
                <Alert severity="info" icon={false}>
                  {NOTA_PEDIDA}
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2">Por qué</Typography>
                <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                  {f.motivos.map((m, i) => (
                    <Typography key={i} component="li" variant="body2">{m}</Typography>
                  ))}
                  {f.error && <Typography component="li" variant="body2" color="error.main">{f.error}</Typography>}
                </Stack>
              </Box>

              {f.mejor && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    La mejor versión en HispaShare
                    {f.hispashareId != null && (
                      <>
                        {' · '}
                        <EnlaceMui component={Link} to={`/pelicula/${f.hispashareId}`}>ver la ficha</EnlaceMui>
                      </>
                    )}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>Resolución</TableCell>
                          <TableCell>Vídeo</TableCell>
                          <TableCell>Audio</TableCell>
                          <TableCell>Subtítulos</TableCell>
                          <TableCell>Tamaño</TableCell>
                          <TableCell align="right">Descargas</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {f.valoracion && (
                          <TableRow>
                            <TableCell>Tu fichero</TableCell>
                            <CeldasDelFichero v={f.valoracion} a={f.analisis} />
                            <TableCell align="right">—</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell>HispaShare</TableCell>
                          <CeldasDeCandidata c={f.mejor} />
                          <TableCell align="right">{f.mejor.downloadCount ?? '—'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {f.plan && (
                <Box>
                  <Typography variant="subtitle2">Lo que haría el conversor</Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                    {describirPlan(f.plan).map((l, i) => (
                      <Typography key={i} component="li" variant="caption" color="text.secondary">{l}</Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
                <Button size="small" startIcon={<LinkIcon />} onClick={onVincular} disabled={ocupada}>
                  {f.hispashareId != null ? 'Cambiar la ficha' : 'Vincular a mano'}
                </Button>
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => onAccion('revisar')} disabled={ocupada}>
                  Revisar de nuevo
                </Button>
                {f.versionesPlexBytes > 0 && (
                  <Tooltip title="Borra la versión optimizada que Plex creó de esta película. Tiene sentido cuando el fichero ya se reproduce directo: si sigue habiendo una regla de «Optimizar» en Plex, la volverá a crear.">
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onBorrarVersiones} disabled={ocupada}>
                      Borrar la Plex Version ({formatearTamano(f.versionesPlexBytes)})
                    </Button>
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary">
                  Revisada el {new Date(f.reviewedAt).toLocaleString('es-ES')}
                  {f.imdbId && ` · ${f.imdbId}`}
                </Typography>
              </Stack>
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

export function Biblioteca() {
  const qc = useQueryClient();
  const [libraryId, setLibraryId] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [busqueda, setBusqueda] = useState('');
  const buscando = busqueda.trim() !== '';
  const [porPagina, setPorPagina] = useState<number>(TAMANOS_PAGINA[0]);
  const [pagina, setPagina] = useState(1);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [abierta, setAbierta] = useState<string | null>(null);
  const [aVincular, setAVincular] = useState<FilaRevision | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'warning' | 'error'; texto: string } | null>(null);
  // Qué Plex Versions se van a borrar, esperando confirmación: es la mitad irreversible.
  const [aBorrar, setABorrar] = useState<FilaRevision[] | null>(null);

  // El sondeo se autodetiene cuando la tarea acaba, como en la watchlist: comparten carril.
  const tarea = useQuery({
    queryKey: ['revision-tarea'],
    queryFn: api.tareaRevision,
    refetchInterval: (q) => (q.state.data?.task?.enCurso ? 1500 : false),
  });
  const enCurso = tarea.data?.task?.enCurso ? tarea.data.task : null;
  const hecha = tarea.data?.task && !tarea.data.task.enCurso && tarea.data.task.nombre === 'revision' ? tarea.data.task : null;

  const datos = useQuery({
    queryKey: ['revision', libraryId],
    queryFn: () => api.revision(libraryId),
    // Mientras corre, la lista va creciendo: se recarga sin esperar al final.
    refetchInterval: enCurso?.nombre === 'revision' ? 5000 : false,
  });

  // La primera biblioteca revisable, si no se ha elegido ninguna.
  const bibliotecas = datos.data?.libraries ?? [];
  useEffect(() => {
    if (!libraryId && bibliotecas.length) setLibraryId(bibliotecas[0]!.id);
  }, [libraryId, bibliotecas]);

  useEffect(() => {
    if (tarea.data?.task?.terminada) void qc.invalidateQueries({ queryKey: ['revision'] });
  }, [qc, tarea.data?.task?.terminada]);

  const lanzar = useMutation({
    mutationFn: (todas: boolean) => api.lanzarRevision(libraryId!, todas),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['revision-tarea'] }),
    onError: (e: ApiFallo) => setAviso({ tipo: 'error', texto: e.message }),
  });

  const accion = useMutation({
    mutationFn: ({ id, tipo }: { id: string; tipo: 'redescargar' | 'convertir' | 'revisar' }) =>
      tipo === 'redescargar'
        ? api.redescargarRevision(id)
        : tipo === 'convertir'
          ? api.convertirRevision(id)
          : api.revisarFila(id),
    onSuccess: (_r, { tipo }) => {
      void qc.invalidateQueries({ queryKey: ['revision'] });
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      if (tipo === 'redescargar') setAviso({ tipo: 'success', texto: 'Encolada. Al colocarla sustituirá al fichero de la biblioteca.' });
      if (tipo === 'convertir') setAviso({ tipo: 'success', texto: 'En la cola de conversión. Al colocarla sustituirá al fichero de la biblioteca.' });
    },
    onError: (e: ApiFallo) => setAviso({ tipo: 'error', texto: e.message }),
  });

  const borrarVersiones = useMutation({
    mutationFn: (filas: FilaRevision[]) =>
      filas.length === 1
        ? api.borrarVersionesPlex(filas[0]!.id).then((r) => ({
            hechas: r.fallos.length ? [] : [filas[0]!.id],
            fallos: r.fallos.map((motivo) => ({ id: filas[0]!.id, motivo })),
            bytes: r.bytes as number | null,
          }))
        : api.revisionEnBloque(filas.map((f) => f.id), 'plex-versions-delete').then((r) => ({ ...r, bytes: null as number | null })),
    onSuccess: (r, filas) => {
      setABorrar(null);
      void qc.invalidateQueries({ queryKey: ['revision'] });
      setMarcadas(new Set(r.fallos.map((f) => f.id)));
      const liberado = r.bytes ?? filas.filter((f) => r.hechas.includes(f.id)).reduce((a, f) => a + f.versionesPlexBytes, 0);
      setAviso(
        r.fallos.length
          ? { tipo: 'warning', texto: `Borradas ${r.hechas.length} (${formatearTamano(liberado)}). ${r.fallos.length} no: ${r.fallos.map((f) => f.motivo).join(' · ')}` }
          : { tipo: 'success', texto: `Borradas las Plex Versions de ${r.hechas.length} película${r.hechas.length === 1 ? '' : 's'}: ${formatearTamano(liberado)} liberados. Plex las quita de su lista al vaciar la papelera.` },
      );
    },
    onError: (e: ApiFallo) => setAviso({ tipo: 'error', texto: e.message }),
  });

  const enBloque = useMutation({
    mutationFn: ({ ids, tipo }: { ids: string[]; tipo: 'convert' | 'redownload' }) => api.revisionEnBloque(ids, tipo),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['revision'] });
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      // Se quedan marcadas las que no arrancaron, con su motivo, como en Conversiones.
      setMarcadas(new Set(r.fallos.map((f) => f.id)));
      setAviso(
        r.fallos.length
          ? { tipo: 'warning', texto: `${r.hechas.length} pedidas. ${r.fallos.length} no: ${r.fallos.map((f) => f.motivo).join(' · ')}` }
          : { tipo: 'success', texto: `${r.hechas.length} pedidas.` },
      );
    },
    onError: (e: ApiFallo) => setAviso({ tipo: 'error', texto: e.message }),
  });

  const todos = datos.data?.items ?? [];
  // La búsqueda va ANTES de los filtros: cada ficha cuenta solo lo que casa con el término.
  const items = buscando ? todos.filter((f) => coincideConBusqueda(f, busqueda)) : todos;
  const resumen = datos.data?.resumen
    ? (Object.fromEntries(ORDEN_VEREDICTOS.map((v) => [v, items.filter((f) => f.veredicto === v).length])) as Record<VeredictoRevision, number>)
    : null;
  const sinCruce = items.filter(sinCruzar).length;
  const filtradas = items
    .filter((f) => (filtro === 'todas' ? true : filtro === 'sin_cruzar' ? sinCruzar(f) : f.veredicto === filtro))
    .sort((a, b) => VEREDICTOS[a.veredicto].orden - VEREDICTOS[b.veredicto].orden || a.title.localeCompare(b.title, 'es'));

  // Paginación en el navegador, sobre lo ya filtrado y buscado: lo que ahoga es PINTAR
  // cientos de filas con sus celdas, no recibirlas.
  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const visibles = filtradas.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);
  const paginado = filtradas.length > TAMANOS_PAGINA[0];
  const cabeceraLista = useRef<HTMLDivElement>(null);
  const irAPagina = (n: number) => {
    setPagina(n);
    cabeceraLista.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  // Otra pestaña, otro término u otra biblioteca son otra lista: se empieza por la primera.
  useEffect(() => {
    setPagina(1);
  }, [filtro, busqueda, porPagina, libraryId]);

  // Se marca lo que se ve; lo marcado en otras páginas sigue contando para la barra.
  const seleccionables = visibles.filter(seleccionable);
  const seleccionadas = filtradas.filter((f) => marcadas.has(f.id));
  const marcar = (id: string, v: boolean) =>
    setMarcadas((prev) => {
      const s = new Set(prev);
      if (v) s.add(id);
      else s.delete(id);
      return s;
    });
  const ocupada = accion.isPending || enBloque.isPending || borrarVersiones.isPending;
  const puedenDescargar = seleccionadas.filter((f) => accionesDe(f).includes('redescargar') && !pedida(f));
  const puedenConvertir = seleccionadas.filter((f) => accionesDe(f).includes('convertir') && !pedida(f));
  const conVersiones = seleccionadas.filter((f) => f.versionesPlexBytes > 0);
  const bytesVersiones = conVersiones.reduce((a, f) => a + f.versionesPlexBytes, 0);
  const biblioteca = bibliotecas.find((b) => b.id === libraryId);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
        <Typography variant="h5">Biblioteca</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="biblioteca-label">Biblioteca</InputLabel>
            <Select
              labelId="biblioteca-label"
              label="Biblioteca"
              value={libraryId ?? ''}
              onChange={(e) => {
                setLibraryId(e.target.value || null);
                setMarcadas(new Set());
                setAbierta(null);
              }}
            >
              {bibliotecas.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.title}
                  {b.revisadas > 0 && ` · ${b.revisadas} revisadas`}
                  {!b.countsAsOwned && ' · no cuenta para la watchlist'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={!libraryId || !!enCurso || lanzar.isPending}
            onClick={() => lanzar.mutate(false)}
          >
            {enCurso?.nombre === 'revision' ? 'Revisando…' : 'Revisar'}
          </Button>
          <Tooltip title="Vuelve a mirar también lo que no ha cambiado desde la última vez. Tarda: un ffprobe por película y una petición por segundo a HispaShare.">
            <span>
              <Button size="small" disabled={!libraryId || !!enCurso || lanzar.isPending} onClick={() => lanzar.mutate(true)}>
                Revisar todo
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {!bibliotecas.length && !datos.isLoading && (
        <Alert severity="info">
          No hay ninguna biblioteca de películas que revisar: en Ajustes tiene que tener ruta local. No hace falta
          que cuente para la watchlist.
        </Alert>
      )}

      {enCurso && (
        <Alert severity="info" icon={false}>
          <Stack spacing={1}>
            <Typography variant="body2">
              {enCurso.nombre === 'revision'
                ? `Revisando «${enCurso.objeto ?? ''}»`
                : enCurso.nombre === 'barrido'
                  ? 'La watchlist está buscando en HispaShare (mismo carril: la revisión espera)'
                  : 'La watchlist está vinculando fichas (mismo carril: la revisión espera)'}
              {' · '}{enCurso.fase}
              {enCurso.total > 0 && ` · ${enCurso.hechos} de ${enCurso.total}`}
            </Typography>
            <LinearProgress
              variant={enCurso.total > 0 ? 'determinate' : 'indeterminate'}
              value={enCurso.total > 0 ? Math.min(100, (enCurso.hechos / enCurso.total) * 100) : undefined}
            />
            <Typography variant="caption" color="text.secondary">Sigue en el servidor aunque cierres esta pestaña.</Typography>
          </Stack>
        </Alert>
      )}

      {hecha?.resumen && (
        <Alert severity={hecha.error ? 'error' : 'success'} onClose={() => qc.setQueryData(['revision-tarea'], { task: null })}>
          «{hecha.resumen.biblioteca}»: {hecha.resumen.revisadas} revisadas
          {hecha.resumen.saltadas ? `, ${hecha.resumen.saltadas} sin cambios` : ''}
          {Object.entries(hecha.resumen.porVeredicto).length > 0 &&
            ` — ${Object.entries(hecha.resumen.porVeredicto)
              .map(([v, n]) => `${n} ${VEREDICTOS[v as VeredictoRevision].etiqueta.toLowerCase()}`)
              .join(', ')}`}
          .{hecha.resumen.errores.length > 0 && ` ${hecha.resumen.errores.length} con problemas: ${hecha.resumen.errores.slice(0, 3).join(' · ')}`}
          {hecha.error && ` ${hecha.error}`}
        </Alert>
      )}

      {aviso && <Alert severity={aviso.tipo} onClose={() => setAviso(null)}>{aviso.texto}</Alert>}

      {resumen && (
        <Stack
          ref={cabeceraLista}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap', scrollMarginTop: 16 }}
          useFlexGap
        >
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', flex: 1 }} useFlexGap>
          <Chip
            label={`Todas · ${items.length}`}
            color={filtro === 'todas' ? 'primary' : 'default'}
            onClick={() => setFiltro('todas')}
          />
          {ORDEN_VEREDICTOS.filter((v) => resumen[v] > 0).map((v) => (
            <Tooltip key={v} title={VEREDICTOS[v].nota}>
              <Chip
                label={`${VEREDICTOS[v].etiqueta} · ${resumen[v]}`}
                color={filtro === v ? VEREDICTOS[v].color === 'default' ? 'primary' : VEREDICTOS[v].color : 'default'}
                variant={filtro === v ? 'filled' : 'outlined'}
                onClick={() => setFiltro(v)}
              />
            </Tooltip>
          ))}
          {/* Otro eje: sin ficha de HispaShare, sea cual sea el veredicto. Va separado de
              los de veredicto porque es lo único que se arregla a mano, vinculando. */}
          {sinCruce > 0 && (
            <Tooltip title="No se pudo cruzar con su ficha de HispaShare: sin IMDb en Plex, sin resultados, o con otro IMDb. Cada fila dice cuál, y se vincula a mano desde su detalle.">
              <Chip
                label={`Sin cruzar · ${sinCruce}`}
                color={filtro === 'sin_cruzar' ? 'primary' : 'default'}
                variant={filtro === 'sin_cruzar' ? 'filled' : 'outlined'}
                onClick={() => setFiltro('sin_cruzar')}
                sx={{ ml: 1 }}
              />
            </Tooltip>
          )}
        </Stack>
        <TextField
          size="small"
          placeholder="Buscar en la lista…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          sx={{ minWidth: { sm: 260 } }}
          slotProps={{
            htmlInput: { 'aria-label': 'Buscar en la lista' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: buscando ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => setBusqueda('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        </Stack>
      )}

      {buscando && filtradas.length === 0 && todos.length > 0 && (
        <Alert severity="info">
          {items.length === 0
            ? `Ninguna película casa con «${busqueda.trim()}».`
            : `Ninguna de las ${items.length} que casan con «${busqueda.trim()}» está en este filtro.`}
        </Alert>
      )}

      {seleccionadas.length > 0 && (
        <Paper sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
            <Typography variant="body2">{seleccionadas.length} seleccionadas</Typography>
            <Button
              size="small"
              variant="contained"
              disabled={!puedenDescargar.length || ocupada}
              onClick={() => enBloque.mutate({ ids: puedenDescargar.map((f) => f.id), tipo: 'redownload' })}
            >
              Descargar {puedenDescargar.length || ''}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!puedenConvertir.length || ocupada}
              onClick={() => enBloque.mutate({ ids: puedenConvertir.map((f) => f.id), tipo: 'convert' })}
            >
              Convertir {puedenConvertir.length || ''}
            </Button>
            {conVersiones.length > 0 && (
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={ocupada} onClick={() => setABorrar(conVersiones)}>
                Borrar {conVersiones.length} Plex Versions · {formatearTamano(bytesVersiones)}
              </Button>
            )}
            <Button size="small" onClick={() => setMarcadas(new Set())}>Quitar la selección</Button>
          </Stack>
        </Paper>
      )}

      {items.length === 0 && biblioteca && !datos.isLoading && !enCurso && (
        <Alert severity="info">
          «{biblioteca.title}» aún no se ha revisado. Pulsa «Revisar»: se lee el inventario de Plex, cada película
          pasa por ffprobe y se busca su ficha en HispaShare, a una petición por segundo.
        </Alert>
      )}

      {visibles.length > 0 && (
        <>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={seleccionables.length > 0 && seleccionables.every((f) => marcadas.has(f.id))}
                    indeterminate={seleccionadas.length > 0 && seleccionadas.length < seleccionables.length}
                    disabled={!seleccionables.length}
                    onChange={(e) => setMarcadas(e.target.checked ? new Set(seleccionables.map((f) => f.id)) : new Set())}
                  />
                </TableCell>
                <TableCell>Película</TableCell>
                <TableCell>Resolución</TableCell>
                <TableCell>Vídeo</TableCell>
                <TableCell>Audio</TableCell>
                <TableCell>Subtítulos</TableCell>
                <TableCell>Bitrate</TableCell>
                <TableCell>Veredicto</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.map((f) => (
                <FilaPelicula
                  key={f.id}
                  f={f}
                  marcada={marcadas.has(f.id)}
                  onMarcar={(v) => marcar(f.id, v)}
                  abierta={abierta === f.id}
                  onAbrir={() => setAbierta(abierta === f.id ? null : f.id)}
                  onAccion={(tipo) => accion.mutate({ id: f.id, tipo })}
                  onVincular={() => setAVincular(f)}
                  onBorrarVersiones={() => setABorrar([f])}
                  ocupada={ocupada}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {paginado && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            useFlexGap
          >
            <Typography variant="caption" color="text.secondary">
              {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, filtradas.length)} de {filtradas.length}
            </Typography>
            <Pagination
              count={totalPaginas}
              page={paginaActual}
              onChange={(_ev, n) => irAPagina(n)}
              color="primary"
              siblingCount={0}
              boundaryCount={1}
            />
            <Select
              size="small"
              value={porPagina}
              onChange={(e) => setPorPagina(Number(e.target.value))}
              inputProps={{ 'aria-label': 'Filas por página' }}
            >
              {TAMANOS_PAGINA.map((n) => (
                <MenuItem key={n} value={n}>
                  {n} por página
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        </>
      )}

      {aBorrar && (
        <Dialog open onClose={() => setABorrar(null)}>
          <DialogTitle>
            {aBorrar.length === 1
              ? `Borrar la Plex Version de «${aBorrar[0]!.title}»`
              : `Borrar las Plex Versions de ${aBorrar.length} películas`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Se borran {formatearTamano(aBorrar.reduce((a, f) => a + f.versionesPlexBytes, 0))} de versiones
              optimizadas que Plex creó. El fichero de la película no se toca. Las teles que dependieran de esa
              versión volverán a pedir transcodificación hasta que el fichero principal sea directo; y si en Plex
              sigue activa una regla de «Optimizar», las volverá a crear.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setABorrar(null)}>Cancelar</Button>
            <Button color="error" variant="contained" disabled={borrarVersiones.isPending} onClick={() => borrarVersiones.mutate(aBorrar)}>
              {borrarVersiones.isPending ? 'Borrando…' : 'Borrar'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {aVincular && (
        <DialogoVincular
          item={{ titulo: aVincular.title, anio: aVincular.year, hispashareId: aVincular.hispashareId }}
          vincular={async (hispashareId) => {
            const r = await api.vincularRevision(aVincular.id, hispashareId);
            void qc.invalidateQueries({ queryKey: ['revision'] });
            // Aquí la discrepancia de IMDb la enseña la propia fila, en sus motivos.
            return { imdbDiscrepa: false, titulo: r.item.title, imdbId: r.item.imdbId ?? '' };
          }}
          onCerrar={() => setAVincular(null)}
        />
      )}
    </Stack>
  );
}
